import React, { useState } from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';
import {
  white,
  red50,
  gray40,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
} from '../../../constants/colors';
import { EMAIL_INPUT_PLACEHOLDER } from '../../../constants/placeholders';
import { useNavigate } from 'react-router-dom';
import CustomTextfield from './../form/CustomTextfield';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { reRegisterPasswordSchema } from '../../../validation/user';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../api/firebase/config';
import CustomSnackbar from './../form/CustomSnackbar';
import CustomLoading from './../form/CustomLoading';

const styles = {
  paper: {
    padding: '40px 40px',
    maxWidth: '398px',
    width: '100%',
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    textAlign: 'center',
    '@media (max-width: 600px)': {
      padding: '32px 16px',
    },
  },
  beforeButton: {
    height: '43px',
    marginTop: '16px',
    width: '100%',
  },
  mailSendButton: {
    height: '43px',
    marginTop: '16px',
    width: '100%',
    fontWeight: 700,
    '@media (max-width: 340px)': {
      fontSize: '12px',
    },
  },
  titleText: {
    textAlign: 'left',
    fontSize: '22px',
    fontWeight: '400',
    marginBottom: '8px',
    whiteSpace: 'pre-wrap',
    '@media (max-width: 600px)': {
      fontSize: '20px',
    },
  },
  messageText: {
    textAlign: 'left',
    fontSize: '16px',
    whiteSpace: 'pre-wrap',
    marginBottom: '16px',
    '@media (max-width: 340px)': {
      fontSize: '14px',
      marginBottom: '32px',
    },
  },
  errorText: {
    color: red50,
    fontSize: '12px',
    textAlign: 'left',
    marginTop: '4px',
  },
};

const ForgetPasswordCard: React.FC = () => {
  const navigate = useNavigate();
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reRegisterPasswordSchema),
  });

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const email = watch('email');

      // SendGrid Cloud Function呼び出し
      const sendPasswordReset = httpsCallable<
        { email: string },
        { success: boolean; message: string }
      >(functions, 'sendPasswordReset');
      const result = await sendPasswordReset({ email });

      if (result.data.success) {
        navigate('/auth/password_reset/complete');
      } else {
        setSnackbarText('パスワードリセットメールの送信に失敗しました。');
        setSnackbarOpen(true);
      }
    } catch (error: unknown) {
      // エラーメッセージの処理
      let errorMessage = 'パスワードリセットメール送信中にエラーが発生しました。';

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };
        if (firebaseError.code === 'functions/not-found') {
          errorMessage = '指定されたメールアドレスのユーザーが見つかりません。';
        } else if (firebaseError.code === 'functions/invalid-argument') {
          errorMessage = 'メールアドレスが正しくありません。';
        }
      }

      setSnackbarText(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const buttonDisabledFlag = (): boolean => {
    if (loading) {
      return true;
    }
    if (firstSubmit) {
      return watch('email') === undefined || '' || Boolean(errors.email);
    }
    return false;
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="body2" gutterBottom sx={styles.titleText}>
          パスワード再設定
        </Typography>
        <Typography variant="body2" sx={styles.messageText}>
          メールアドレスを入力後、認証を行います。
        </Typography>
        <Controller
          render={({ field }): JSX.Element => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.email)}
              formMb="24px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              placeholder={EMAIL_INPUT_PLACEHOLDER}
              requiredText="必須"
              width="396px"
            />
          )}
          control={control}
          name="email"
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Button
            onClick={(): void => {
              navigate('/');
            }}
            variant="outlined"
            sx={[secondaryButtonBackgroundColor, styles.beforeButton]}
          >
            戻る
          </Button>
          <Button
            disabled={buttonDisabledFlag()}
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => onSubmit())();
            }}
            variant="contained"
            sx={[primaryButtonBackgroundColor, styles.mailSendButton]}
          >
            メール送信
          </Button>
        </Box>
      </Paper>
      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarText}
      />
    </>
  );
};

export default ForgetPasswordCard;
